<template>
  <page hide-footer :loading="$apollo.loading" :err="fatalErr">
    <b-row>
      <b-col cols="12" md="8" offset-md="2">

        <b-media class="my-3" right-align>
          <template v-slot:aside v-if="company && company.siteBranding && company.siteBranding.smallLogo && company.siteBranding.smallLogo.fileLocation">
            <b-img height="32" width="32" :src="company.siteBranding.smallLogo.fileLocation"/>
          </template>
          <h3>Invite people to <strong>{{ company && company.siteBranding && company.siteBranding.name ? company.siteBranding.name : company.name }}</strong></h3>
        </b-media>

        <b-card class="shadow p-0" v-if="showInviteSuccess">
          <h3>Invites sent!</h3>
          <p>Your {{ inviteRows.length | plural('invite') }} have been sent.</p>
          <p>An email will be with them shortly with more details.</p>
          <b-button @click="resetForm" variant="outline-primary">Invite more</b-button>
        </b-card>
        <b-form v-else>
          <template v-if="showInviteHelper">
            <b-row>
              <b-col cols="6">
                <p class="ml-1"><strong>Email Address</strong></p>
              </b-col>
              <b-col cols="5">
                <p class="ml-1"><strong>Name (Optional)</strong></p>
              </b-col>
            </b-row>
            <template v-if="inviteRows.length">
              <b-row v-for="(invite, i) in inviteRows" :key="i">
                <b-col cols="12" sm="6">
                  <b-form-group>
                    <b-form-input placeholder="name@example.com" type="email" v-model.trim="invite.emailAddress"
                      :class="{ 'is-invalid': typeof invite.err === 'string' }"/>
                    <div v-if="typeof invite.err === 'string'" class="invalid-feedback">
                      <template v-if="invite.err === 'USER_ALREADY_EXISTS'">This email address has already signed up to <strong>{{ company && company.siteBranding && company.siteBranding.name ? company.siteBranding.name : company.name }}</strong></template>
                      <template v-else-if="invite.err === 'INVITE_ALREADY_EXISTS'">This email address has already been invited to <strong>{{ company && company.siteBranding && company.siteBranding.name ? company.siteBranding.name : company.name }}</strong></template>
                      <template v-else>{{ invite.err }}</template>
                    </div>
                  </b-form-group>
                </b-col>
                <b-col cols="10" sm="5">
                  <b-form-group>
                    <b-form-input placeholder="Optional" type="text" v-model.trim="invite.name"/>
                  </b-form-group>
                </b-col>
                <b-col cols="2" sm="1">
                  <b-button @click="removeRow(i)" :disabled="inviteRows.length <= 1" variant="outline-warning">&times;</b-button>
                </b-col>
              </b-row>
            </template>
            <template v-else>
              <p class="mx-2">Add some rows to get started</p>
            </template>
          </template>
          <template v-else>
            <b-form-group label="Email Addresses">
              <b-form-textarea placeholder="Enter something..." rows="6" v-model="inviteBlock"/>
              <b-form-text>Please separate multiple addresses with commas, or on new lines.</b-form-text>
            </b-form-group>
          </template>
          <b-row class="mb-3" v-if="isRecruiterAdmin">
            <b-col cols="12" sm="6">
              <b-form-group label="From Name (Optional)">
                <b-form-input :placeholder="user.fullName" type="text" v-model="fromName"/>
                <b-form-text>Optionally set the From name in emails, defaults to <strong>your name</strong>.</b-form-text>
              </b-form-group>
            </b-col>
            <b-col cols="12" sm="6">
              <b-form-group label="Role">
                <b-form-select :options="newRoleOptions" placeholder="Optional" v-model="role"/>
                <b-form-text>Optionally set the role of these users, defaults to <strong>Employee</strong>.</b-form-text>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" sm="9" v-if="showInviteHelper">
              <p class="ml-2"><a href="#" @click.prevent="() => addRow()">&plus; Add another</a> or <a href="#" @click.prevent="() => showInviteHelper = !showInviteHelper">add many at once</a></p>
            </b-col>
            <b-col cols="12" sm="9" v-else>
              <p class="ml-2"><a href="#" @click.prevent="() => showInviteHelper = !showInviteHelper">&larr; Add individually</a></p>
            </b-col>
            <b-col cols="12" sm="3" class="text-right">
              <b-button @click="sendInvites()" variant="outline-primary">Send Invitations</b-button>
            </b-col>
          </b-row>
        </b-form>

        <!-- Search Bar -->
        <div>
          <b-form-group>
            <b-form-input id="search-bar" placeholder="Search Users" v-model="searchTerm"/>
            <!-- <b-btn @click="testLog()">Console</b-btn> -->
          </b-form-group>
        </div>

        <b-card class="shadow mt-3 p-0" id="existing-table" no-body v-if="filterAllUsers">
          <b-table table-class="m-0" hover striped responsive show-empty :fields="existingUserInviteFields" :items="filterAllUsers"
            :current-page="invitePage" :per-page="inviteLimit" primary-key="entry.id">
              <template #empty>
                <p class="text-center">Nobody has been invited... yet?</p>
              </template>
              <template #cell(opts)="{ item: { entry: { id: inviteID } } }">
                <b-btn @click.prevent="() => deleteInvite(inviteID)" variant="outline-danger">
                  <font-awesome-icon icon="trash-alt"/>
                </b-btn>
              </template>
          </b-table>
          <div class="d-flex justify-content-center align-items-center">
            <b-pagination
              v-model="invitePage"
              :per-page="inviteLimit"
              :total-rows="(filterAllUsers && Array.isArray(filterAllUsers) ? filterAllUsers.length : 0)"
            />
          </div>
        </b-card>

      </b-col>
    </b-row>
  </page>
</template>

<script>
import formatDate from 'date-fns/format';
import Page from '@/components/Page';
import UserInvitesCompanyQuery from '@/graphql/queries/UserInvitesCompany.gql';
import UserInvitesUserQuery from '@/graphql/queries/UserInvitesUser.gql';
import UserInvitesDeleteMutation from '@/graphql/mutations/UserInvitesDelete.gql';
import UserInvitesUpsertMutation from '@/graphql/mutations/UserInvitesUpsert.gql';
import { mapRoot } from '@src/mixins/root';

export default {
  components: {
    Page,
  },
  data: () => ({
    searchTerm: null,
    fatalErr: null,
    showInviteHelper: true,
    showInviteSuccess: false,
    fromName: null,
    role: null,
    inviteRows: [],
    inviteBlock: '',
    inviteWhere: {},
    inviteOrder: 'CREATED_ASC',
    invitePage: 1,
    inviteLimit: 100,
  }),
  apollo: {
    existingUserInvites: {
      query() {
        return this.isRecruiterAdmin ? UserInvitesCompanyQuery : UserInvitesUserQuery;
      },
      variables() {
        const { inviteWhere, inviteOrder } = this;
        return { where: inviteWhere, order: inviteOrder };
      },
      update({ company, user }) {
        if (this.isRecruiterAdmin) {
          return company.userInviteConnections;
        } else {
          return user.userInviteConnections;
        }
      },
    },
  },
  metaInfo: {
    title: 'Invite people',
  },
  created() {
    this.resetForm();
  },
  computed: {
    ...mapRoot([ 'company', 'user', 'isRecruiter', 'isRecruiterAdmin' ]),
    newRoleOptions() {
      const roles = [
        { text: 'Employee', value: null },
        // { text: 'Alumni', value: 'ALUMNI' },
        { text: 'Recruiter', value: 'RECRUITER' },
      ];

      if (this.isRecruiterAdmin) {
        roles.push({ text: 'Recruiter Admin', value: 'RECRUITER_ADMIN' });
      }

      return roles;
    },
    filterAllUsers() {
      if (!this.existingUserInvites || !Array.isArray(this.existingUserInvites.edges)) {
        return [];
      } else if (this.searchTerm === null || this.searchTerm.length < 2) {
        return this.existingUserInvites.edges;
      } else {
        const enteredSearchTerm = this.searchTerm.trim().toLowerCase();
        return this.existingUserInvites.edges.filter(({ entry: e }) => {
          if (!e) return false;
          return (e.name || '').trim().toLowerCase().includes(enteredSearchTerm) || e.emailAddress.trim().toLowerCase().includes(enteredSearchTerm);
        });
      }
    },
    existingUserInviteFields() {
      const { isRecruiterAdmin, user: { id: userID } } = this;
      return [
        {
          key: 'entry.emailAddress',
          label: 'Email Address',
        },
        {
          key: 'entry.name',
          label: 'Name',
        },
        isRecruiterAdmin
          ? {
            key: 'entry.fromName',
            label: 'Invited By',
            formatter(value, key, { entry: { user } }) {
              const { id, fullName } = user || {};
              return value || (id === userID ? 'You' : fullName) || null;
            },
          } : null,
        {
          key: 'entry.created',
          label: 'Invited At',
          formatter: value => formatDate(value, 'YYYY-MM-DD HH:mm'),
        },
        {
          key: 'entry.status',
          label: 'Status',
          formatter(value, key, { entry: { emailStatus } }) {
            switch (value) {
              case 'INVITED': switch (emailStatus) {
                case 'SENT': return 'Email sent';
                case 'DELIVERED': return 'Email delivered';
                case 'OPENED': return 'Email opened';
                case 'CLICKED': return 'Link clicked';
                case 'BOUNCED': return 'Email bounced';
                case 'COMPLAINT': return 'Marked as spam';
                case 'UNSUBSCRIBED': return 'Unsubscribed from future invites';
                default: return 'Invited';
              }
              case 'ACCEPTED': return 'Accepted';
              case 'DECLINED': return 'Declined';
              case 'DELETED': return 'Deleted';
              default: return null;
            }
          },
        },
        {
          key: 'opts',
          label: '',
          headerTitle: 'Options',
          thStyle: { width: '50px' },
        },
      ];
    },
  },
  methods: {
    // testLog() {
    //   console.log('userInvites IS: ', this.existingUserInvites);
    //   console.log(this.searchTerm);
    //   console.log(this.filterAllUsers);
    // },
    addRow() {
      this.inviteRows.push({});
    },
    removeRow(i) {
      this.inviteRows.splice(i, 1);
    },
    resetForm() {
      this.inviteRows = [ {}, {}, {} ];
      this.inviteBlock = null;
      this.showInviteHelper = true;
      this.showInviteSuccess = false;
    },
    async sendInvites() {
      try {
        const { isRecruiterAdmin, fromName, role } = this;

        const people = [];
        if (this.showInviteHelper) {
          this.inviteRows.filter(r => r && r.emailAddress).forEach(row => people.push(row));
          this.inviteBlock = people.map(({ emailAddress }) => emailAddress).join('\n');
        } else {
          (this.inviteBlock.match(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}\b/igm) || [])
            .forEach(emailAddress => people.push({ emailAddress }));
          this.inviteRows = people.map(({ emailAddress }) => ({ emailAddress }));
        }

        this.$log.debug(people);

        if (people.length) {
          try {
            await this.$apollo.mutate({
              mutation: UserInvitesUpsertMutation,
              variables: {
                upsert: people.map(({ name, emailAddress }) => ({
                  create: { name, emailAddress, ...(isRecruiterAdmin ? { fromName, role } : {}) },
                  where: { emailAddress },
                })),
              },
            });

            this.inviteWhere = {};
            // this.invitePage = 1;
            await this.$apollo.queries.existingUserInvites.refetch();

            // inviteRows is overwritten so we can report which emailAddresses had invites sent to them
            this.showInviteSuccess = true;
            this.inviteRows = people;
          } catch (err) {
            this.showInviteHelper = true;

            // If there is a conflict, mark the row for the user to fix
            if (Array.isArray(err.graphQLErrors) && err.graphQLErrors.find(({ extensions: { code } }) => code === 'USER_ALREADY_EXISTS')) {
              const { extensions: { code, existing } } = err.graphQLErrors.find(({ extensions: { code } }) => code === 'USER_ALREADY_EXISTS');
              this.inviteRows = this.inviteRows.map(row => {
                if (row.emailAddress && existing.includes(row.emailAddress)) {
                  row.err = code;
                }
                return row;
              });
            }

            throw err;
          }
        } else {
          this.resetForm();
        }
      } catch (err) {
        this.$notifyErr(err, { prepend: 'Failed to send invites:' });
      }
    },
    async deleteInvite(id) {
      try {
        const { isRecruiterAdmin } = this;
        const query = this.$options.apollo.existingUserInvites.query.call(this);
        const variables = this.$options.apollo.existingUserInvites.variables.call(this);

        await this.$apollo.mutate({
          mutation: UserInvitesDeleteMutation,
          variables: { where: { id } },
          update(store) {
            const data = store.readQuery({ query, variables });

            const connections = isRecruiterAdmin ? data.company.userInviteConnections : data.user.userInviteConnections;
            connections.edges = connections.edges.filter(({ entry: { id: entryID } }) => entryID !== id);

            console.log(connections.edges);

            store.writeQuery({ query, variables, data });
          },
        });
      } catch (err) {
        this.$notifyErr(err, { prepend: 'Failed to delete invite' });
      }
    },
  },
  watch: {
    invitePage() {
      this.$scrollTo('#existing-table');
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/User/UsersInvite.vue